import { useMemo, CSSProperties } from 'react'
import Select, {
	components,
	InputActionMeta,
	StylesConfig,
	Theme,
} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { createGuid } from '../lib/utils'
import useTranslation from '../lib/lang/useTranslation'
import {
	GrayStatusIcon,
	GreenStatusIcon,
	RedStatusIcon,
	YellowStatusIcon,
} from '@app/assets/icons'
import { Box } from '@mui/material'
import { themeColors } from '@app/constants'

export interface CustomSelectProps {
	creatable?: boolean
	clearable?: boolean
	disabled?: boolean
	multiple?: boolean
	styles?: CSSProperties
	onInputChange?:
		| ((newValue: string, actionMeta: InputActionMeta) => void)
		| undefined
	options: any[] | undefined
	onCreate?: (inputValue: string) => void
	onChange?:
		| (((value: any, actionMeta: any) => void) &
				((value: any, action: any) => void))
		| undefined
	onKeyDown?: React.KeyboardEventHandler
	placeholder?: string
	selected?: any
	labelTransform?: (option: any) => string
	labelKey?: string
	valueKey?: string
	id?: string
	name?: string
	size?: 'mini' | 'tiny' | 'small' | 'large'
	className?: any
	filterOption?: () => boolean
	width?: string | number
	minWidth?: string | number
	height?: number
	customStyles?: StylesConfig<any, false>
	getSelectTheme?: (theme: Theme) => Theme
	inputValue?: string
	menuIsOpen?: boolean
	onMenuOpen?: () => void
	onMenuClose?: () => void
	onFocus?: () => void
	ref?: any
	disableBorder?: boolean
	showStatusIcon?: boolean
}

export default function CustomSelect({
	clearable,
	creatable,
	disabled,
	labelTransform,
	labelKey = 'label',
	name,
	multiple = false,
	options,
	onCreate,
	onChange,
	onInputChange,
	onKeyDown,
	id,
	styles = {},
	placeholder = 'shared.defaultSelectPlaceholder',
	selected = null,
	size = 'large',
	valueKey = 'value',
	className,
	width,
	minWidth,
	height: heightFromProps,
	filterOption,
	customStyles: customStylesFromProps,
	getSelectTheme: getSelectThemeFromProps,
	inputValue,
	menuIsOpen,
	onMenuOpen,
	onMenuClose,
	onFocus,
	ref,
	disableBorder = false,
	showStatusIcon = true,
}: CustomSelectProps) {
	const height =
		heightFromProps ??
		(size === 'mini'
			? 30
			: size === 'tiny'
			? 36
			: size === 'small'
			? 36
			: 51)

	const customStyles: StylesConfig<any, false> = (customStylesFromProps ?? {
		valueContainer: (base) => ({
			...base,
			height: height,
			...(width && {
				width: width,
			}),
		}),
		menuPortal: (base) => ({
			...base,
			zIndex: 9999,
		}),
		control: (base, { isDisabled }) => ({
			...base,
			...styles,
			...(width && {
				width: width,
			}),
			...(minWidth && {
				minWidth: minWidth,
			}),
			backgroundColor:
				styles.backgroundColor ??
				(disabled ? themeColors.white : base.backgroundColor),
			...(disableBorder
				? {
						border: 'none',
						boxShadow: 'none',
						'&:hover': {
							border: 'none',
							boxShadow: 'none',
						},
						'&:focus-within': {
							border: 'none',
							boxShadow: 'none',
						},
				  }
				: {
						borderColor: '#00000044',
						'&:hover': {
							borderColor: themeColors.agitronDark,
						},
				  }),
			padding: 0,
		}),
		indicatorSeparator: () => ({
			display: 'none',
		}),
		clearIndicator: (base) => ({
			...base,

			padding: 0,
		}),
		dropdownIndicator: (base, { isDisabled }) => ({
			...base,
			padding: 0,
			paddingRight: size === 'mini' ? '0px' : '12px',
			paddingLeft: size === 'mini' ? '0px' : '8px',
		}),
		menuList: (base) => {
			return {
				...base,
				maxHeight: 300,
			}
		},
		option: (base, { isDisabled, isFocused, isSelected, hasValue }) => {
			let backgroundColor = base.backgroundColor
			let color = base.color

			if (isFocused && !isSelected) {
				backgroundColor = '#f9f9f9'
			}

			return {
				...base,
				backgroundColor: backgroundColor,
				color: color,
			}
		},
	}) as StylesConfig<any, false>

	const { t } = useTranslation()
	const valueId = useMemo(
		() => (id || name || createGuid()) + '-value',
		[id, name]
	)
	const inputId = useMemo(
		() => (id || name || createGuid()) + '-input',
		[id, name]
	)
	const clearId = useMemo(
		() => (id || name || createGuid()) + '-clear',
		[id, name]
	)

	const formatCreateLabel = (label: string) => {
		return t('shared.createLabel', {
			label,
		})
	}

	const getSelectTheme =
		getSelectThemeFromProps ??
		((theme: Theme): Theme => {
			return {
				...theme,
				borderRadius: 4,
				spacing: {
					baseUnit: size === 'large' ? 6 : 4,
				} as any,
				colors: {
					...theme.colors,
					primary25: 'lightgrey',
					primary: '#1b2c36',
				},
			}
		})

	const onCreateOption = (inputValue: string) => {
		if (onCreate) {
			onCreate(inputValue)
		}
	}

	const sxBox = {
		flexDirection: 'row',
		display: 'flex',
		alignItems: 'center',
		height: size === 'large' ? height - 22 : height - 4,
		...(size === 'mini' && {
			fontSize: 12,
		}),
	}

	const {
		Option,
		SingleValue,
		Placeholder,
		MultiValue,
		IndicatorsContainer,
	} = components
	const IconOption = (props: any) => (
		<Option {...props}>
			<Box
				sx={{
					...sxBox,
					fontSize: '12px',
				}}
			>
				{showStatusIcon ? (
					props.label == 'Running' ||
					props.label == 'Success' ||
					props.label == 'Complete' ||
					props.label == 'Online' ||
					props.label == 'No alarm' ? (
						<GreenStatusIcon />
					) : props.label == 'Refreshing' ||
					  props.label == 'Processing' ||
					  props.label == 'Need attention' ||
					  props.label == 'Normal alarm' ? (
						<YellowStatusIcon />
					) : props.label == 'Error' ||
					  props.label == 'Offline' ||
					  props.label == 'Express alarm' ||
					  props.label == 'Expiration alarm' ? (
						<RedStatusIcon />
					) : props.label == 'Registered' ||
					  props.label == 'All transactions' ||
					  props.label == 'All statuses' ? (
						<GrayStatusIcon />
					) : null
				) : null}
				{props.label}
			</Box>
		</Option>
	)

	const IconSelected = (props: any) => (
		<SingleValue {...props}>
			<Box sx={sxBox} data-testid={valueId}>
				{showStatusIcon ? (
					props.data.label == 'Running' ||
					props.data.label == 'Success' ||
					props.data.label == 'Complete' ||
					props.data.label == 'Online' ||
					props.data.label == 'No alarm' ? (
						<GreenStatusIcon />
					) : props.data.label == 'Refreshing' ||
					  props.data.label == 'Processing' ||
					  props.data.label == 'Need attention' ||
					  props.data.label == 'Normal alarm' ? (
						<YellowStatusIcon />
					) : props.data.label == 'Error' ||
					  props.data.label == 'Offline' ||
					  props.data.label == 'Express alarm' ||
					  props.data.label == 'Expiration alarm' ? (
						<RedStatusIcon />
					) : size !== 'mini' &&
					  (props.data.label == 'Registered' ||
							props.data.label == 'All transactions' ||
							props.data.label == 'All statuses') ? (
						<GrayStatusIcon />
					) : null
				) : null}
				{props.data[labelKey]}
			</Box>
		</SingleValue>
	)

	const PlaceholderWithIcon = (props: any) => (
		<Placeholder {...props}>
			<Box sx={sxBox}>
				{size !== 'mini' &&
				(placeholder == 'All Statuses' ||
					placeholder == 'All transactions') ? (
					<GrayStatusIcon />
				) : null}
				{t(placeholder)}
			</Box>
		</Placeholder>
	)

	const MultiValueWithId = (props: any) => (
		<MultiValue {...props}>
			<div data-testid={valueId}>{props.children}</div>
		</MultiValue>
	)

	const IndicatorsContainerWithId = (props: any) => (
		<IndicatorsContainer {...props}>
			<div
				data-testid={clearId}
				style={{ display: 'contents' }}
				onClick={(e) => e.stopPropagation()}
			>
				{props.children}
			</div>
		</IndicatorsContainer>
	)

	if (creatable) {
		return (
			<CreatableSelect
				ref={ref}
				menuPortalTarget={document.body}
				value={selected}
				isClearable={clearable}
				name={name}
				inputId={inputId}
				instanceId={1}
				menuPlacement="auto"
				formatCreateLabel={formatCreateLabel}
				onInputChange={onInputChange}
				onKeyDown={onKeyDown}
				menuIsOpen={menuIsOpen}
				onMenuOpen={onMenuOpen}
				onMenuClose={onMenuClose}
				isDisabled={disabled}
				isMulti={multiple as any}
				styles={customStyles}
				options={options}
				onCreateOption={onCreateOption}
				onFocus={onFocus}
				onChange={onChange}
				getOptionLabel={(option) => option[labelKey]}
				getOptionValue={(option) => option[valueKey]}
				theme={getSelectTheme}
				inputValue={inputValue}
				components={{
					Option: IconOption,
					SingleValue: IconSelected,
					Placeholder: PlaceholderWithIcon,
					MultiValue: MultiValueWithId,
					IndicatorsContainer: IndicatorsContainerWithId,
				}}
				isOptionDisabled={(option) => {
					return option.isDisabled
				}}
			/>
		)
	}

	if (!labelTransform) {
		labelTransform = (option: any) => option[labelKey]
	}

	return (
		<Select
			ref={ref}
			menuPortalTarget={document.body}
			filterOption={filterOption}
			value={selected}
			isClearable={clearable}
			name={name}
			inputId={inputId}
			instanceId={1}
			menuPlacement="auto"
			isMulti={multiple as any}
			onInputChange={onInputChange}
			onKeyDown={onKeyDown}
			isDisabled={disabled}
			styles={customStyles}
			menuIsOpen={menuIsOpen}
			onMenuOpen={onMenuOpen}
			onMenuClose={onMenuClose}
			options={options}
			onFocus={onFocus}
			onChange={onChange}
			getOptionLabel={labelTransform}
			getOptionValue={(option) => option[valueKey]}
			theme={getSelectTheme}
			className={className}
			inputValue={inputValue}
			components={{
				Option: IconOption,
				SingleValue: IconSelected,
				Placeholder: PlaceholderWithIcon,
				MultiValue: MultiValueWithId,
				IndicatorsContainer: IndicatorsContainerWithId,
			}}
			isOptionDisabled={(option) => {
				if (option.isDisabled) return true
				else return false
			}}
		/>
	)
}
